import React from 'react';
import { Link } from 'react-router-dom';
import EventStatus from './EventStatus';

const EventCard = ({ event, viewMode = 'grid' }) => {
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getLevelIcon = (level) => {
    const icons = {
      'LL': 'fas fa-child',
      'SJ': 'fas fa-user-friends',
      'JL': 'fas fa-users',
      'SL': 'fas fa-trophy'
    };
    return icons[level] || 'fas fa-baseball-ball';
  };

  const getTypeIcon = (type) => {
    return type === 'IN' ? 'fas fa-globe' : 'fas fa-flag';
  };

  const getDaysUntilEvent = () => {
    const today = new Date();
    const eventDate = new Date(event.event_start_date);
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return null;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `${diffDays} days`;
  };

  const daysUntil = getDaysUntilEvent();

  if (viewMode === 'list') {
    return (
      <div className="event-card list-view">
        <div className="event-card-content">
          <div className="event-image">
            {event.event_image ? (
              <img src={event.event_image} alt={event.event_name} />
            ) : (
              <div className="event-image-placeholder">
                <i className="fas fa-baseball-ball"></i>
              </div>
            )}
          </div>

          <div className="event-details">
            <div className="event-header">
              <div className="event-badges">
                <span className={`badge badge-level ${event.event_level}`}>
                  <i className={getLevelIcon(event.event_level)}></i>
                  {event.level_name}
                </span>
                <span className={`badge badge-type ${event.event_type}`}>
                  <i className={getTypeIcon(event.event_type)}></i>
                  {event.type_name}
                </span>
                {event.is_featured && (
                  <span className="badge badge-featured">
                    <i className="fas fa-star"></i>
                    Featured
                  </span>
                )}
              </div>
              
              <EventStatus event={event} />
            </div>

            <h3 className="event-title">
              <Link to={`/events/${event.slug || event.id}`}>
                {event.event_name}
              </Link>
            </h3>

            <div className="event-meta">
              <div className="event-date">
                <i className="fas fa-calendar-alt"></i>
                <span>{event.formatted_dates}</span>
                {daysUntil && (
                  <span className="days-until">({daysUntil})</span>
                )}
              </div>

              <div className="event-venue">
                <i className="fas fa-map-marker-alt"></i>
                <span>{event.event_venue}</span>
                {event.event_city && (
                  <span>, {event.event_city}</span>
                )}
              </div>

              <div className="event-organizer">
                <i className="fas fa-users"></i>
                <span>Organized by {event.event_organizer}</span>
              </div>
            </div>

            {event.event_description && (
              <p className="event-description">
                {event.event_description.length > 150
                  ? `${event.event_description.substring(0, 150)}...`
                  : event.event_description
                }
              </p>
            )}

            <div className="event-footer">
              <div className="event-registration">
                {event.registration_status === 'open' && (
                  <div className="registration-open">
                    <i className="fas fa-check-circle text-success"></i>
                    <span>Registration Open</span>
                    {event.registration_end_date && (
                      <span className="registration-deadline">
                        (Until {formatDate(event.registration_end_date)})
                      </span>
                    )}
                  </div>
                )}
                
                {event.registration_status === 'closed' && (
                  <div className="registration-closed">
                    <i className="fas fa-times-circle text-danger"></i>
                    <span>Registration Closed</span>
                  </div>
                )}

                {event.max_teams && (
                  <div className="team-count">
                    <i className="fas fa-users"></i>
                    <span>{event.registered_teams}/{event.max_teams} teams</span>
                  </div>
                )}
              </div>

              <div className="event-actions">
                <Link 
                  to={`/events/${event.slug || event.id}`}
                  className="btn btn-primary btn-sm"
                >
                  View Details
                  <i className="fas fa-arrow-right"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid view (default)
  return (
    <div className="event-card grid-view">
      <div className="event-image">
        {event.event_image ? (
          <img src={event.event_image} alt={event.event_name} />
        ) : (
          <div className="event-image-placeholder">
            <i className="fas fa-baseball-ball"></i>
          </div>
        )}
        
        <div className="event-image-overlay">
          <div className="event-badges">
            <span className={`badge badge-level ${event.event_level}`}>
              <i className={getLevelIcon(event.event_level)}></i>
              {event.level_name}
            </span>
            <span className={`badge badge-type ${event.event_type}`}>
              <i className={getTypeIcon(event.event_type)}></i>
              {event.type_name}
            </span>
          </div>
          
          {event.is_featured && (
            <div className="featured-badge">
              <i className="fas fa-star"></i>
            </div>
          )}

          {daysUntil && (
            <div className="countdown-badge">
              {daysUntil}
            </div>
          )}
        </div>
      </div>

      <div className="event-content">
        <div className="event-header">
          <EventStatus event={event} />
        </div>

        <h3 className="event-title">
          <Link to={`/events/${event.slug || event.id}`}>
            {event.event_name}
          </Link>
        </h3>

        <div className="event-meta">
          <div className="event-date">
            <i className="fas fa-calendar-alt"></i>
            <span>{event.formatted_dates}</span>
          </div>

          <div className="event-venue">
            <i className="fas fa-map-marker-alt"></i>
            <span>{event.event_venue}</span>
          </div>

          <div className="event-organizer">
            <i className="fas fa-users"></i>
            <span>{event.event_organizer}</span>
          </div>
        </div>

        {event.event_description && (
          <p className="event-description">
            {event.event_description.length > 100
              ? `${event.event_description.substring(0, 100)}...`
              : event.event_description
            }
          </p>
        )}

        <div className="event-footer">
          <div className="event-info">
            {event.registration_status === 'open' && (
              <div className="registration-status open">
                <i className="fas fa-check-circle"></i>
                <span>Registration Open</span>
              </div>
            )}
            
            {event.max_teams && (
              <div className="team-progress">
                <div className="team-count">
                  {event.registered_teams}/{event.max_teams} teams
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ 
                      width: `${Math.min((event.registered_teams / event.max_teams) * 100, 100)}%` 
                    }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          <div className="event-actions">
            <Link 
              to={`/events/${event.slug || event.id}`}
              className="btn btn-primary btn-block"
            >
              View Details
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCard;